/**
 * /assessment-purchased — Square checkout return page.
 *
 * On mount:
 *   1. Reads tier + Square order id + email + first_name from the URL.
 *   2. Calls `POST /api/assessment-purchases/` (idempotent on square_order_id).
 *   3. Persists the resulting `purchase_token` + tier in sessionStorage so the
 *      tier shell on `/clinical-assessment` can read it.
 *   4. Renders the confirmation card with "Begin Assessment" CTA.
 *
 * Square Payment Link "Confirmation page redirect" should point here, with
 * `?tier=intro|standard|premium` baked into each link in the Square dashboard.
 * Square will append `?order_id=...` automatically when configured.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import {
  assessmentPurchaseApi,
  persistTierContext,
} from "@/lib/assessmentPurchaseApi";

export default function AssessmentPurchased() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const tier = (params.get("tier") || "").toLowerCase();
  const squareOrderId =
    params.get("order_id") || params.get("square_order_id") || params.get("order") || "";
  const purchaseLinkUsed = params.get("purchase_link_used") || "";
  const email = params.get("email") || "";
  const firstName = params.get("first_name") || params.get("name") || "";

  const [state, setState] = useState({ status: "loading", purchase: null, meta: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Sensible default: if no tier in URL but `purchase_link_used` is set,
        // the backend will derive the tier from the Square URL.
        if (!tier && !purchaseLinkUsed) {
          throw new Error("We couldn't match this checkout to an assessment level. If you completed a payment, please contact the clinic and we'll get you started right away.");
        }
        const r = await assessmentPurchaseApi.create({
          assessment_tier: tier,
          email: email || undefined,
          first_name: firstName || undefined,
          purchase_link_used: purchaseLinkUsed || undefined,
          square_order_id: squareOrderId || undefined,
          payment_source: "square_payment_link",
          attribution: {
            utm_source: params.get("utm_source") || undefined,
            utm_medium: params.get("utm_medium") || undefined,
            utm_campaign: params.get("utm_campaign") || undefined,
            presenting_context: (params.get("context") || (typeof window !== "undefined" && window.localStorage.getItem("kissed_presenting_context")) || undefined),
            deep_link_base: typeof window !== "undefined" ? window.location.origin : undefined,
          },
        });
        if (cancelled) return;
        const purchase = r.data.purchase;
        const tierResp = await assessmentPurchaseApi.byToken(purchase.purchase_token).catch(() => null);
        if (cancelled) return;
        const meta = tierResp?.data?.tier_metadata || null;

        // Cache the tier + token so the next route (`/clinical-assessment`)
        // can pick them up without query-string juggling.
        persistTierContext({
          tier: purchase.assessment_tier,
          purchase_token: purchase.purchase_token,
          email: purchase.email || "",
          first_name: purchase.first_name || "",
          captured_at: new Date().toISOString(),
        });
        setState({ status: "ok", purchase, meta, error: null });
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "We couldn't record your purchase right now.";
        setState({ status: "error", purchase: null, meta: null, error: msg });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beginHref = useMemo(() => {
    if (!state.purchase) return "/clinical-assessment";
    const q = new URLSearchParams();
    q.set("tier", state.purchase.assessment_tier);
    q.set("ref", state.purchase.purchase_token);
    return `/clinical-assessment?${q.toString()}`;
  }, [state.purchase]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/60 via-white to-purple-50/30 flex flex-col" data-testid="assessment-purchased">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KISSED Wellness Assessment™</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-14">
        {state.status === "loading" && (
          <div className="text-center py-20 text-slate-600" data-testid="ap-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-purple-900" />
            <p className="mt-4 text-sm">Confirming your purchase…</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-2xl border border-red-200 bg-red-50/60 p-6" data-testid="ap-error">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <h2 className="font-display text-lg text-red-800">We couldn't record your purchase</h2>
                <p className="mt-2 text-sm text-red-700">{state.error}</p>
                <div className="mt-4 flex gap-2">
                  <Link to="/clinical-assessment">
                    <Button variant="outline" data-testid="ap-error-back">Back to tier selection</Button>
                  </Link>
                  <a href="mailto:c.von@kissedbycvon.com?subject=KCIS%20Purchase%20Issue">
                    <Button variant="ghost" data-testid="ap-error-contact">Contact admissions</Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {state.status === "ok" && state.purchase && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-600 flex items-center justify-center shadow-lg" data-testid="ap-checkmark">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <span className="block mt-4 text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold">
                Purchase confirmed
              </span>
              <h1 className="mt-2 font-display text-3xl sm:text-4xl text-slate-900" data-testid="ap-headline">
                Thank you{state.purchase.first_name ? `, ${state.purchase.first_name}` : ""}.
              </h1>
              <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-xl mx-auto" data-testid="ap-sub">
                Your <strong>{state.meta?.label || state.purchase.assessment_tier}</strong> is ready. You can begin your assessment whenever you're ready — your responses are saved as you go.
              </p>
            </div>

            <div className="rounded-2xl border border-purple-200 bg-white p-6 shadow-sm" data-testid="ap-summary">
              <div className="text-[10px] uppercase tracking-[0.18em] text-purple-900 font-semibold mb-3">
                Your purchase
              </div>
              <dl className="grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-slate-500">Tier</dt>
                <dd className="text-slate-900 font-medium" data-testid="ap-tier">{state.meta?.label || state.purchase.assessment_tier}</dd>
                <dt className="text-slate-500">Amount</dt>
                <dd className="text-slate-900 font-medium" data-testid="ap-amount">{state.purchase.amount_display || state.meta?.price_display || "—"}</dd>
                <dt className="text-slate-500">Purchase date</dt>
                <dd className="text-slate-900 font-medium" data-testid="ap-date">{formatDate(state.purchase.purchase_date)}</dd>
                {state.purchase.email ? (
                  <>
                    <dt className="text-slate-500">Email</dt>
                    <dd className="text-slate-900 font-medium" data-testid="ap-email">{state.purchase.email}</dd>
                  </>
                ) : null}
                {state.purchase.square_order_id ? (
                  <>
                    <dt className="text-slate-500">Order reference</dt>
                    <dd className="text-slate-900 font-medium" data-testid="ap-order">{state.purchase.square_order_id}</dd>
                  </>
                ) : null}
              </dl>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="ap-included">
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-semibold mb-3">
                What you'll receive
              </div>
              <ul className="space-y-2 text-sm text-slate-800">
                {(state.meta?.includes || []).map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                size="lg"
                className="flex-1 bg-purple-900 hover:bg-purple-800 text-white rounded-full"
                onClick={() => navigate(beginHref)}
                data-testid="ap-begin-btn"
              >
                Begin Assessment
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Link to="/" className="flex-1">
                <Button variant="outline" size="lg" className="w-full rounded-full" data-testid="ap-home-btn">
                  Return to KISSED
                </Button>
              </Link>
            </div>

            <div className="rounded-2xl border border-purple-200 bg-white p-5 text-sm text-slate-700" data-testid="ap-carehub">
              <div className="font-semibold text-slate-900">Your Client Care Hub</div>
              <p className="mt-1">Sign in anytime with <strong>{state.purchase.email || "the email used at checkout"}</strong> to resume this assessment, see your purchases and access what your practitioner releases to you. A one-time passcode is emailed to you — no password needed.</p>
              <Link to="/client/login" className="mt-3 inline-block font-semibold text-purple-800 hover:underline" data-testid="ap-carehub-link">Go to Client Care Hub →</Link>
            </div>

            <p className="text-center text-xs text-slate-500 italic" data-testid="ap-quote">
              "We kiss the mind, teach the wisdom, and make the mission better."
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
